<template>
  <div>
    <Header />
    <v-container>
      <v-row class="mb-2" align="center">
        <v-col cols="12" sm="auto">
          <h2 class="title">
            <v-icon class="mr-2">mdi-needle</v-icon>
            Stickerei – Produktionsliste
            <v-chip class="ml-2" small>{{ items.length }} Aufträge</v-chip>
          </h2>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="auto">
          <v-btn small @click="$refs.shelfDialog.open()">
            <v-icon left small>mdi-wardrobe-outline</v-icon>
            Regal
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn small @click="$refs.productsDialog.open()">
            <v-icon left small>mdi-format-list-bulleted</v-icon>
            Produkte
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn small :color="hotfolderName ? 'success' : 'warning'" @click="selectHotfolder">
            <v-icon left small>mdi-folder-open</v-icon>
            {{ hotfolderName ? 'Hotfolder: ' + hotfolderName : 'Hotfolder wählen' }}
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn small @click="fetchEntries" :loading="loading">
            <v-icon left small>mdi-refresh</v-icon>
            Aktualisieren
          </v-btn>
        </v-col>
      </v-row>

      <v-row v-if="loading">
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col v-for="group in skuGroups" :key="group.sku" cols="6" sm="4" md="3" lg="2">
          <v-card raised style="border-radius: 6px; cursor: pointer;" @click="openDialog(group)">
            <v-img :src="skuImageUrl(group.sku)" height="120px" contain class="grey lighten-3">
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-icon size="48" color="grey lighten-1">mdi-tshirt-crew</v-icon>
                </v-row>
              </template>
            </v-img>
            <v-card-text class="pa-2 text-center">
              <div style="font-size: 11px; font-weight: bold; word-break: break-all; line-height: 1.3;">
                {{ group.sku }}
              </div>
              <div class="mt-1">
                <v-chip color="primary" small>
                  {{ group.scanned }}/{{ group.count - group.exported }} gescannt
                </v-chip>
                <v-chip v-if="group.exported > 0" color="purple" dark small class="ml-1">
                  {{ group.exported }} exp.
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="!loading && skuGroups.length === 0" cols="12">
          <v-alert type="info">Keine Aufträge in der Produktionsliste.</v-alert>
        </v-col>
      </v-row>
    </v-container>

    <!-- Main Dialog -->
    <v-dialog v-model="dialog.show" max-width="640px" @keydown.esc="dialog.show = false">
      <v-card v-if="dialog.show">
        <v-card-title class="pb-0">
          <v-icon class="mr-2">mdi-needle</v-icon>
          {{ dialog.sku }}
          <v-chip class="ml-2" small color="primary">
            {{ dialog.scannedCount }}/{{ dialog.orders.length }} gescannt
          </v-chip>
          <v-chip v-if="dialog.exportedCount > 0" class="ml-1" small color="purple" dark>
            {{ dialog.exportedCount }} exportiert
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-tabs v-model="dialog.tab" class="mt-2">
          <v-tab>
            <v-icon left small>mdi-barcode-scan</v-icon>
            Scannen
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-printer</v-icon>
            Drucken
          </v-tab>
          <v-tab :disabled="dialog.scannedCount === 0">
            <v-icon left small>mdi-file-delimited</v-icon>
            CSV Export
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="dialog.tab">

          <!-- ── Tab 0: Scan ── -->
          <v-tab-item>
            <v-card-text>
              <v-alert v-if="dialog.scanError" type="error" dense dismissible @input="dialog.scanError = ''">
                {{ dialog.scanError }}
              </v-alert>
              <v-alert v-if="dialog.scanSuccess" type="success" dense>
                {{ dialog.scanSuccess }}
              </v-alert>

              <div class="scan-hint mb-3">
                <v-icon color="primary" class="mr-1">mdi-barcode-scan</v-icon>
                <span>Scanne das gedruckte Label (UUID-Barcode). Der Status wird automatisch aktualisiert.</span>
              </div>

              <!-- Hidden scanner input -->
              <input
                ref="scannerInput"
                type="text"
                style="position:absolute;left:-9999px;width:1px;height:1px;opacity:0;"
                autocomplete="off"
                @keydown="onScanKeydown"
              />

              <v-list dense>
                <v-list-item v-for="order in dialog.orders" :key="order.id">
                  <v-list-item-icon>
                    <v-icon small :color="order.status === 'exported' ? 'purple' : order.status === 'scanned' ? 'success' : 'grey'">
                      {{ order.status === 'exported' ? 'mdi-check-all' : order.status === 'scanned' ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title style="font-size: 12px;">
                      {{ order.orderId }}
                      <span v-if="order.line1" style="color:#888; font-weight:normal;"> – {{ order.line1 }}</span>
                    </v-list-item-title>
                    <v-list-item-subtitle style="font-size: 10px; font-family: monospace;">{{ order.uuid }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip x-small :color="order.status === 'exported' ? 'purple' : order.status === 'scanned' ? 'success' : 'grey lighten-2'" :dark="order.status === 'exported'">
                      {{ order.status === 'exported' ? 'exportiert' : order.status === 'scanned' ? 'OK' : 'ausstehend' }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="dialog.show = false">Schließen</v-btn>
              <v-spacer></v-spacer>
              <v-btn v-if="dialog.scannedCount > 0" color="success" @click="dialog.tab = 2">
                <v-icon left>mdi-file-delimited</v-icon>
                Zum CSV Export ({{ dialog.scannedCount }})
              </v-btn>
            </v-card-actions>
          </v-tab-item>

          <!-- ── Tab 1: Print ── -->
          <v-tab-item>
            <v-card-text>
              <v-alert type="info" dense class="mb-3">
                Es wird ein Label pro Auftrag gedruckt ({{ dialog.orders.length }} Labels).
                Drucker: Brother WKL 12
              </v-alert>
              <v-list dense style="max-height: 280px; overflow-y: auto;">
                <v-list-item v-for="order in dialog.orders" :key="order.id">
                  <v-list-item-icon><v-icon small>mdi-tag</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title style="font-size: 12px;">{{ order.orderId }}</v-list-item-title>
                    <v-list-item-subtitle style="font-size: 10px; font-family: monospace;">{{ order.uuid }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="dialog.show = false" :disabled="dialog.printing">Abbrechen</v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="printLabels" :loading="dialog.printing" :disabled="dialog.printing">
                <v-icon left>mdi-printer</v-icon>
                {{ dialog.orders.length }} Labels drucken
              </v-btn>
            </v-card-actions>
          </v-tab-item>

          <!-- ── Tab 2: CSV Export ── -->
          <v-tab-item>
            <v-card-text>
              <v-alert v-if="dialog.scannedCount < dialog.orders.length - dialog.exportedCount" type="info" dense class="mb-3">
                {{ dialog.scannedCount }} von {{ dialog.orders.length - dialog.exportedCount }} ausstehenden Labels gescannt. Nur gescannte werden exportiert.
              </v-alert>
              <v-alert v-else type="success" dense class="mb-3">
                {{ dialog.scannedCount }} Labels bereit für den CSV-Export.
              </v-alert>

              <v-alert v-if="!hotfolderHandle" type="warning" dense class="mb-3">
                Kein Hotfolder gewählt. CSV wird als normaler Download gespeichert.
              </v-alert>
              <v-alert v-else type="success" dense class="mb-3">
                CSV wird direkt in Hotfolder gespeichert: <strong>{{ hotfolderName }}</strong>
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="dialog.show = false" :disabled="dialog.downloading">Abbrechen</v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="success"
                @click="downloadCsv"
                :loading="dialog.downloading"
                :disabled="dialog.downloading || dialog.scannedCount === 0"
              >
                <v-icon left>mdi-file-delimited</v-icon>
                CSV exportieren ({{ dialog.scannedCount }} Aufträge)
              </v-btn>
            </v-card-actions>
          </v-tab-item>

        </v-tabs-items>
      </v-card>
    </v-dialog>

    <EmbroideryProductsDialog ref="productsDialog" />
    <EmbroideryShelfDialog ref="shelfDialog" />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="4000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Header from "../header";
import productionService from "@/services/ProductionService";
import EmbroideryProductsDialog from "@/components/Embroidery/EmbroideryProductsDialog";
import EmbroideryShelfDialog from "@/components/Embroidery/EmbroideryShelfDialog";

const HOTFOLDER_KEY = 'embroidery_hotfolder_name';

export default {
  name: "EmbroideryV1",
  components: { Header, EmbroideryProductsDialog, EmbroideryShelfDialog },
  data: () => ({
    items: [],
    loading: false,
    hotfolderHandle: null,
    hotfolderName: '',
    dialog: {
      show: false,
      tab: 0,
      sku: "",
      orders: [],
      scannedCount: 0,
      exportedCount: 0,
      scanError: '',
      scanSuccess: '',
      printing: false,
      downloading: false,
    },
    snackbar: { show: false, text: "", color: "success" },

    // Scanner key buffer (for barcode scanners sending char-by-char)
    _scanBuffer: '',
    _scanTimer: null,
  }),
  computed: {
    skuGroups() {
      const map = {};
      for (const item of this.items) {
        const sku = item.sku || "(kein SKU)";
        if (!map[sku]) map[sku] = { sku, count: 0, scanned: 0, exported: 0, orders: [] };
        map[sku].count++;
        if (item.status === 'scanned') map[sku].scanned++;
        if (item.status === 'exported') map[sku].exported++;
        map[sku].orders.push(item);
      }
      return Object.values(map).sort((a, b) => b.count - a.count);
    },
  },
  async mounted() {
    await this.fetchEntries();
    this.hotfolderName = localStorage.getItem(HOTFOLDER_KEY) || '';
  },
  methods: {
    async fetchEntries() {
      this.loading = true;
      try {
        const response = await productionService.getEmbroideryProductionList();
        this.items = response.data;
      } catch (error) {
        console.error("Error fetching embroidery production list:", error);
      } finally {
        this.loading = false;
      }
    },

    skuImageUrl(sku) {
      return `https://backoffice.accelery.de/api/files/sku?sku=${encodeURIComponent(sku)}`;
    },

    async selectHotfolder() {
      if (!window.showDirectoryPicker) {
        this.showSnackbar('Browser unterstützt kein Ordner-Picker (Chrome empfohlen)', 'error');
        return;
      }
      try {
        const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
        this.hotfolderHandle = handle;
        this.hotfolderName = handle.name;
        localStorage.setItem(HOTFOLDER_KEY, handle.name);
        this.showSnackbar(`✅ Hotfolder gesetzt: ${handle.name}`, 'success');
      } catch (err) {
        if (err.name !== 'AbortError') this.showSnackbar('❌ Fehler beim Ordner wählen', 'error');
      }
    },

    openDialog(group) {
      this.dialog.sku = group.sku;
      this.dialog.orders = [...group.orders];
      this.dialog.scannedCount = group.scanned;
      this.dialog.exportedCount = group.exported;
      this.dialog.tab = 0;
      this.dialog.scanError = '';
      this.dialog.scanSuccess = '';
      this.dialog.printing = false;
      this.dialog.downloading = false;
      this.dialog.show = true;
      this.$nextTick(() => this.$refs.scannerInput?.focus());
    },

    // ── Scanner input (barcode reader sends chars one by one + Enter) ──────
    onScanKeydown(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        const val = this._scanBuffer.trim();
        this._scanBuffer = '';
        clearTimeout(this._scanTimer);
        if (val) this.processScan(val);
        return;
      }
      if (e.key && e.key.length === 1) {
        e.preventDefault();
        this._scanBuffer += e.key;
        clearTimeout(this._scanTimer);
        this._scanTimer = setTimeout(() => {
          const val = this._scanBuffer.trim();
          this._scanBuffer = '';
          if (val) this.processScan(val);
        }, 300);
      }
    },

    async processScan(scannedValue) {
      this.dialog.scanError = '';
      this.dialog.scanSuccess = '';

      // Find matching pending order by uuid (exact) or orderId
      const match = this.dialog.orders.find(o =>
        o.status === 'pending' && (o.uuid === scannedValue || String(o.orderId) === scannedValue)
      );

      if (!match) {
        const done = this.dialog.orders.find(o =>
          ['scanned', 'exported'].includes(o.status) && (o.uuid === scannedValue || String(o.orderId) === scannedValue)
        );
        if (done) {
          this.dialog.scanError = `Bereits ${done.status === 'exported' ? 'exportiert' : 'gescannt'}: ${done.orderId}`;
        } else {
          this.dialog.scanError = `Nicht gefunden: ${scannedValue}`;
        }
        this.$nextTick(() => this.$refs.scannerInput?.focus());
        return;
      }

      try {
        await productionService.scanEmbroideryProductionItem(match.uuid, match.sku, null);
        // Update local state
        match.status = 'scanned';
        this.dialog.scannedCount++;
        // Also update in items array
        const inItems = this.items.find(i => i.id === match.id);
        if (inItems) inItems.status = 'scanned';

        this.dialog.scanSuccess = `✅ ${match.orderId} gescannt`;
        setTimeout(() => { this.dialog.scanSuccess = ''; }, 2000);

        const pendingLeft = this.dialog.orders.filter(o => o.status === 'pending').length;
        if (pendingLeft === 0) {
          this.showSnackbar(`✅ Alle ausstehenden Labels gescannt!`, 'success');
        }
      } catch (err) {
        const msg = err.response?.data?.error || 'Fehler beim Scannen';
        this.dialog.scanError = msg;
      }
      this.$nextTick(() => this.$refs.scannerInput?.focus());
    },

    // ── Print ───────────────────────────────────────────────────────────────
    async printLabels() {
      this.dialog.printing = true;
      try {
        const orders = this.dialog.orders.map(o => ({
          orderId: o.orderId,
          sku: o.sku,
          uuid: o.uuid,
          line1: o.line1 || null,
          line2: o.line2 || null,
          line3: o.line3 || null,
          table: o.sourceTable || null,
        }));
        const response = await productionService.printEmbroideryLabels(orders);
        const { printed, failed } = response.data;
        if (failed.length === 0) {
          this.showSnackbar(`✅ ${printed.length} Labels gedruckt`, "success");
        } else {
          this.showSnackbar(`⚠️ ${printed.length} gedruckt, ${failed.length} fehlgeschlagen`, "warning");
        }
        this.dialog.show = false;
        await this.fetchEntries();
      } catch (err) {
        console.error("Error printing embroidery labels:", err);
        this.showSnackbar("❌ Fehler beim Drucken", "error");
      } finally {
        this.dialog.printing = false;
      }
    },

    // ── CSV Export ──────────────────────────────────────────────────────────
    async downloadCsv() {
      this.dialog.downloading = true;
      try {
        if (!this.hotfolderHandle && this.hotfolderName && window.showDirectoryPicker) {
          try {
            this.hotfolderHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
            this.hotfolderName = this.hotfolderHandle.name;
            localStorage.setItem(HOTFOLDER_KEY, this.hotfolderHandle.name);
          } catch (err) {
            if (err.name !== 'AbortError') console.warn('Could not re-acquire hotfolder handle:', err);
          }
        }

        const response = await productionService.downloadEmbroideryCsv(this.dialog.sku);
        const csvBlob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const safeSku = this.dialog.sku.replace(/[^a-zA-Z0-9_-]/g, '_');
        const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const fileName = `${safeSku}_${date}.csv`;

        if (this.hotfolderHandle) {
          try {
            const fileHandle = await this.hotfolderHandle.getFileHandle(fileName, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(csvBlob);
            await writable.close();
            this.showSnackbar(`✅ CSV in Hotfolder gespeichert: ${fileName}`, 'success');
            this.dialog.show = false;
            await this.fetchEntries();
            return;
          } catch (err) {
            console.warn('Hotfolder write failed, falling back to download:', err);
            this.hotfolderHandle = null;
            this.showSnackbar('⚠️ Hotfolder-Zugriff verloren – CSV wird heruntergeladen.', 'warning');
          }
        }

        const url = window.URL.createObjectURL(csvBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
        this.showSnackbar('✅ CSV exportiert', 'success');
        this.dialog.show = false;
        await this.fetchEntries();
      } catch (err) {
        const msg = err.response?.data?.error || 'Fehler beim CSV-Export';
        this.showSnackbar(`❌ ${msg}`, 'error');
      } finally {
        this.dialog.downloading = false;
      }
    },

    showSnackbar(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
.scan-hint {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #555;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 10px 14px;
}
</style>
